import React, { useState, useEffect } from 'react'
import { api, formatDate } from '../../../utils/api'
import { getStageName } from '../../../utils/dealConstants'

/**
 * CustomerModal - Shows customer activity, deals, and orders for an email
 */
function CustomerModal({ email, onClose }) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [selectedItem, setSelectedItem] = useState(null)
  const [itemDetails, setItemDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)

  useEffect(() => {
    if (email) {
      loadCustomerData()
    }
  }, [email])

  async function loadCustomerData() {
    setLoading(true)
    try {
      const response = await api.get(`/api/tickets/customer/${encodeURIComponent(email)}`)
      setData(response)
    } catch (error) {
      console.error('Failed to load customer data:', error)
    } finally {
      setLoading(false)
    }
  }

  async function selectItem(type, id, basicData) {
    setSelectedItem({ type, id, data: basicData })
    setItemDetails(null)
    setLoadingDetails(true)

    try {
      if (type === 'deal') {
        const response = await api.get(`/api/deals/${id}`)
        setItemDetails(response.deal || response)
      } else if (type === 'order') {
        // Use shopifyId if available for Shopify orders, otherwise fallback
        const shopifyId = basicData?.shopifyId || id
        const response = await api.get(`/api/orders/shopify/${shopifyId}`)
        setItemDetails(response.order || response)
      } else if (type === 'ticket') {
        const response = await api.get(`/api/tickets/${id}`)
        setItemDetails(response.ticket || response)
      }
    } catch (error) {
      console.error(`Failed to load ${type} details:`, error)
      setItemDetails(basicData) // Fall back to basic data
    } finally {
      setLoadingDetails(false)
    }
  }

  if (!email) return null

  return (
    <div className="modalOverlay">
      <div className="modalContent" onClick={e => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: '85vh' }}>
        <div className="modalHead">
          <h2>Customer: {email}</h2>
          <button className="modalClose" onClick={onClose}>&times;</button>
        </div>
        <div className="modalBody" style={{ padding: 0, overflow: 'auto', maxHeight: 'calc(85vh - 60px)' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <div className="loading-spinner" />
            </div>
          ) : (
            <div>
              {/* Tickets */}
              <div style={{ borderBottom: '1px solid rgba(0,0,0,.08)' }}>
                <div style={{ padding: '12px 20px', background: 'var(--bg)', fontWeight: 600, fontSize: 13 }}>
                  Tickets ({data?.tickets?.length || 0})
                </div>
                {data?.tickets?.length > 0 ? (
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {data.tickets.map(ticket => (
                      <div
                        key={ticket.id}
                        style={{
                          padding: '10px 20px',
                          borderBottom: '1px solid rgba(0,0,0,.04)',
                          cursor: 'pointer',
                          background: selectedItem?.type === 'ticket' && selectedItem?.id === ticket.id ? 'var(--bg)' : 'white'
                        }}
                        onClick={() => selectItem('ticket', ticket.id, ticket)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 500 }}>{ticket.subject || '(No subject)'}</span>
                          <span className={`badge ${(ticket.status === 'new' || ticket.status === 'awaiting_reply') ? 'badge-success' : ticket.status === 'resolved' ? '' : 'badge-secondary'}`}>
                            {ticket.status}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                          {formatDate(ticket.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 20, color: 'var(--muted)', textAlign: 'center' }}>No tickets found</div>
                )}
              </div>

              {/* Deals */}
              <div style={{ borderBottom: '1px solid rgba(0,0,0,.08)' }}>
                <div style={{ padding: '12px 20px', background: 'var(--bg)', fontWeight: 600, fontSize: 13 }}>
                  Deals ({data?.deals?.length || 0})
                </div>
                {data?.deals?.length > 0 ? (
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {data.deals.map(deal => (
                      <div
                        key={deal.id}
                        style={{
                          padding: '10px 20px',
                          borderBottom: '1px solid rgba(0,0,0,.04)',
                          cursor: 'pointer',
                          background: selectedItem?.type === 'deal' && selectedItem?.id === deal.id ? 'var(--bg)' : 'white'
                        }}
                        onClick={() => selectItem('deal', deal.id, deal)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 500 }}>{deal.name}</span>
                          {deal.amount && <span style={{ fontWeight: 600 }}>£{parseFloat(deal.amount).toFixed(2)}</span>}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                          {getStageName(deal.stage)} • {formatDate(deal.createdAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 20, color: 'var(--muted)', textAlign: 'center' }}>No deals found</div>
                )}
              </div>

              {/* Orders */}
              <div style={{ borderBottom: '1px solid rgba(0,0,0,.08)' }}>
                <div style={{ padding: '12px 20px', background: 'var(--bg)', fontWeight: 600, fontSize: 13 }}>
                  Orders ({data?.orders?.length || 0})
                </div>
                {data?.orders?.length > 0 ? (
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {data.orders.map(order => (
                      <div
                        key={order.id || order.shopifyId}
                        style={{
                          padding: '10px 20px',
                          borderBottom: '1px solid rgba(0,0,0,.04)',
                          cursor: 'pointer',
                          background: selectedItem?.type === 'order' && selectedItem?.id === (order.id || order.shopifyId) ? 'var(--bg)' : 'white'
                        }}
                        onClick={() => selectItem('order', order.shopifyId || order.id, order)}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontWeight: 500 }}>{order.id || order.name}</span>
                          <span style={{ fontWeight: 600 }}>£{parseFloat(order.total || 0).toFixed(2)}</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                          {order.platform || 'Shopify'} • {order.status || 'unfulfilled'} • {formatDate(order.created || order.createdAt)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 20, color: 'var(--muted)', textAlign: 'center' }}>No orders found</div>
                )}
              </div>

              {/* Activities */}
              <div>
                <div style={{ padding: '12px 20px', background: 'var(--bg)', fontWeight: 600, fontSize: 13 }}>
                  Activities ({data?.activities?.length || 0})
                </div>
                {data?.activities?.length > 0 ? (
                  <div style={{ maxHeight: 200, overflow: 'auto' }}>
                    {data.activities.map(activity => (
                      <div
                        key={activity.id}
                        style={{
                          padding: '10px 20px',
                          borderBottom: '1px solid rgba(0,0,0,.04)'
                        }}
                      >
                        <div style={{ fontWeight: 500 }}>{activity.title}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                          {activity.activity_type} • {formatDate(activity.created_at)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ padding: 20, color: 'var(--muted)', textAlign: 'center' }}>No activities found</div>
                )}
              </div>

              {/* Detail Panel */}
              {selectedItem && (
                <div style={{ borderTop: '2px solid var(--primary)', background: '#f8fafc', padding: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 14 }}>
                      {selectedItem.type === 'ticket' && 'Ticket Details'}
                      {selectedItem.type === 'deal' && 'Deal Details'}
                      {selectedItem.type === 'order' && 'Order Details'}
                    </h3>
                    <button className="btn btn-sm btn-ghost" onClick={() => { setSelectedItem(null); setItemDetails(null); }}>Close</button>
                  </div>

                  {loadingDetails ? (
                    <div style={{ textAlign: 'center', padding: 20 }}>
                      <div className="loading-spinner" style={{ width: 24, height: 24 }} />
                    </div>
                  ) : (
                    <>
                      {selectedItem.type === 'ticket' && (
                        <div style={{ fontSize: 13 }}>
                          <p><strong>Subject:</strong> {(itemDetails || selectedItem.data).subject || '(No subject)'}</p>
                          <p><strong>Status:</strong> {(itemDetails || selectedItem.data).status}</p>
                          <p><strong>Priority:</strong> {(itemDetails || selectedItem.data).priority}</p>
                          <p><strong>Created:</strong> {formatDate((itemDetails || selectedItem.data).created_at)}</p>
                          {(itemDetails || selectedItem.data).resolved_at && <p><strong>Resolved:</strong> {formatDate((itemDetails || selectedItem.data).resolved_at)}</p>}
                          {itemDetails?.body_text && (
                            <div style={{ marginTop: 12, padding: 12, background: 'white', borderRadius: 6, maxHeight: 150, overflow: 'auto' }}>
                              <strong style={{ display: 'block', marginBottom: 8 }}>Message:</strong>
                              <pre style={{ whiteSpace: 'pre-wrap', margin: 0, fontSize: 12 }}>{itemDetails.body_text}</pre>
                            </div>
                          )}
                        </div>
                      )}

                      {selectedItem.type === 'deal' && (
                        <div style={{ fontSize: 13 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                            <p style={{ margin: 0 }}><strong>Deal Name:</strong></p>
                            <p style={{ margin: 0 }}>{(itemDetails || selectedItem.data).dealname || selectedItem.data.name}</p>

                            <p style={{ margin: 0 }}><strong>Customer:</strong></p>
                            <p style={{ margin: 0 }}>{itemDetails?.customer_name || '-'}</p>

                            <p style={{ margin: 0 }}><strong>Email:</strong></p>
                            <p style={{ margin: 0 }}>{itemDetails?.customer_email || '-'}</p>

                            <p style={{ margin: 0 }}><strong>Stage:</strong></p>
                            <p style={{ margin: 0 }}>{getStageName((itemDetails || selectedItem.data).dealstage || selectedItem.data.stage)}</p>

                            <p style={{ margin: 0 }}><strong>Amount:</strong></p>
                            <p style={{ margin: 0 }}>£{parseFloat((itemDetails || selectedItem.data).amount || 0).toFixed(2)}</p>

                            <p style={{ margin: 0 }}><strong>Created:</strong></p>
                            <p style={{ margin: 0 }}>{formatDate((itemDetails || selectedItem.data).created_at || selectedItem.data.createdAt)}</p>

                            {itemDetails?.carrier && (
                              <>
                                <p style={{ margin: 0 }}><strong>Carrier:</strong></p>
                                <p style={{ margin: 0 }}>{itemDetails.carrier}</p>
                              </>
                            )}

                            {itemDetails?.tracking_number && (
                              <>
                                <p style={{ margin: 0 }}><strong>Tracking:</strong></p>
                                <p style={{ margin: 0 }}>
                                  <a
                                    href={
                                      itemDetails.carrier?.toLowerCase().includes('fedex')
                                        ? `https://www.fedex.com/fedextrack/?trknbr=${itemDetails.tracking_number}`
                                        : itemDetails.carrier?.toLowerCase().includes('dhl')
                                        ? `https://www.dhl.com/gb-en/home/tracking.html?tracking-id=${itemDetails.tracking_number}`
                                        : itemDetails.carrier?.toLowerCase().includes('ups')
                                        ? `https://www.ups.com/track?tracknum=${itemDetails.tracking_number}`
                                        : `https://www.royalmail.com/track-your-item#/tracking-results/${itemDetails.tracking_number}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#2563eb', textDecoration: 'underline' }}
                                  >
                                    {itemDetails.tracking_number}
                                  </a>
                                </p>
                              </>
                            )}

                            {itemDetails?.collection_date && (
                              <>
                                <p style={{ margin: 0 }}><strong>Collection Date:</strong></p>
                                <p style={{ margin: 0 }}>{formatDate(itemDetails.collection_date)}</p>
                              </>
                            )}
                          </div>

                          {itemDetails?.items && itemDetails.items.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                              <strong>Items ({itemDetails.items.length}):</strong>
                              <div style={{ marginTop: 8, maxHeight: 150, overflow: 'auto', background: 'white', borderRadius: 6 }}>
                                {itemDetails.items.map((item, i) => (
                                  <div key={i} style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,.05)', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{item.title || item.name || `Item ${i + 1}`}</span>
                                    <span style={{ fontWeight: 500 }}>£{parseFloat(item.price || item.amount || 0).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div style={{ marginTop: 12 }}>
                            <a
                              href={`/deals/all?email=${encodeURIComponent(email)}`}
                              className="btn btn-sm btn-primary"
                            >
                              View in Deals
                            </a>
                          </div>
                        </div>
                      )}

                      {selectedItem.type === 'order' && (
                        <div style={{ fontSize: 13 }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
                            <p style={{ margin: 0 }}><strong>Order ID:</strong></p>
                            <p style={{ margin: 0 }}>{(itemDetails || selectedItem.data).name || (itemDetails || selectedItem.data).order_id || selectedItem.data.id}</p>

                            <p style={{ margin: 0 }}><strong>Customer:</strong></p>
                            <p style={{ margin: 0 }}>{itemDetails?.customer?.first_name} {itemDetails?.customer?.last_name || ''}{!itemDetails?.customer?.first_name && (selectedItem.data.buyer || '-')}</p>

                            <p style={{ margin: 0 }}><strong>Email:</strong></p>
                            <p style={{ margin: 0 }}>{itemDetails?.email || itemDetails?.customer?.email || '-'}</p>

                            <p style={{ margin: 0 }}><strong>Platform:</strong></p>
                            <p style={{ margin: 0 }}>{(itemDetails || selectedItem.data).platform || 'Shopify'}</p>

                            <p style={{ margin: 0 }}><strong>Total:</strong></p>
                            <p style={{ margin: 0 }}>£{parseFloat((itemDetails || selectedItem.data).total_price || (itemDetails || selectedItem.data).total || 0).toFixed(2)}</p>

                            <p style={{ margin: 0 }}><strong>Fulfillment:</strong></p>
                            <p style={{ margin: 0 }}>{(itemDetails || selectedItem.data).fulfillment_status || (itemDetails || selectedItem.data).status || 'unfulfilled'}</p>

                            <p style={{ margin: 0 }}><strong>Financial:</strong></p>
                            <p style={{ margin: 0 }}>{itemDetails?.financial_status || (itemDetails || selectedItem.data).financial || '-'}</p>

                            <p style={{ margin: 0 }}><strong>Created:</strong></p>
                            <p style={{ margin: 0 }}>{formatDate((itemDetails || selectedItem.data).created_at || selectedItem.data.created)}</p>

                            {/* Tracking info from fulfillments */}
                            {itemDetails?.fulfillments?.length > 0 && itemDetails.fulfillments[0]?.tracking_number && (
                              <>
                                <p style={{ margin: 0 }}><strong>Carrier:</strong></p>
                                <p style={{ margin: 0 }}>{itemDetails.fulfillments[0].tracking_company || '-'}</p>

                                <p style={{ margin: 0 }}><strong>Tracking:</strong></p>
                                <p style={{ margin: 0 }}>
                                  {itemDetails.fulfillments[0].tracking_url ? (
                                    <a
                                      href={itemDetails.fulfillments[0].tracking_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: '#2563eb', textDecoration: 'underline' }}
                                    >
                                      {itemDetails.fulfillments[0].tracking_number}
                                    </a>
                                  ) : (
                                    itemDetails.fulfillments[0].tracking_number
                                  )}
                                </p>
                              </>
                            )}
                          </div>

                          {itemDetails?.line_items && itemDetails.line_items.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                              <strong>Items ({itemDetails.line_items.length}):</strong>
                              <div style={{ marginTop: 8, maxHeight: 150, overflow: 'auto', background: 'white', borderRadius: 6 }}>
                                {itemDetails.line_items.map((item, i) => (
                                  <div key={i} style={{ padding: '8px 12px', borderBottom: '1px solid rgba(0,0,0,.05)', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{item.title || item.sku || `Item ${i + 1}`}</span>
                                    <span style={{ fontWeight: 500 }}>£{parseFloat(item.price || 0).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div style={{ marginTop: 12 }}>
                            <a
                              href={`/orders?order_id=${(itemDetails || selectedItem.data).name || selectedItem.data.id}`}
                              className="btn btn-sm btn-primary"
                            >
                              View in Orders
                            </a>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CustomerModal
